import React, { useEffect, useMemo, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';

import tenants from '~/consts/tenant';

import { DEFAULT_TRANSLATION_CATALOG_COLUMNS, buildTranslationCatalogDescriptions } from './translationCatalogUtils';
import type { TranslationCatalogCreatePayload } from './types';

const DEFAULT_BRAND = tenants.lux.brand;
const DEFAULT_COLUMNS = DEFAULT_TRANSLATION_CATALOG_COLUMNS.join('\n');
const TRANSLATION_CATALOG_TENANT_KEYS = ['lux', 'cudo', 'scoopon', 'deals', 'treatme', 'leagenthub'];
const BRANDS = TRANSLATION_CATALOG_TENANT_KEYS.map((tenantKey) => tenants[tenantKey].brand);

interface Props {
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onCreate: (payload: TranslationCatalogCreatePayload) => void;
}

function parseColumns(value: string): Array<string> {
  return value
    .split(/[\n,]/)
    .map((columnName) => columnName.trim())
    .filter(Boolean);
}

export default function NewCatalogDialog({ open, isSubmitting, onClose, onCreate }: Props) {
  const [brand, setBrand] = useState<string>(DEFAULT_BRAND);
  const [catalogName, setCatalogName] = useState('');
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  const parsedColumns = useMemo(() => parseColumns(columns), [columns]);
  const canCreate = catalogName.trim().length > 0 && parsedColumns.length > 0;

  useEffect(() => {
    if (!open) return;

    setBrand(DEFAULT_BRAND);
    setCatalogName('');
    setColumns(DEFAULT_COLUMNS);
  }, [open]);

  const handleCreate = () => {
    const columnNames = parsedColumns;
    onCreate({
      brand,
      catalogName: catalogName.trim(),
      columnNames,
      descriptions: buildTranslationCatalogDescriptions(columnNames),
      sourceValues: columnNames.reduce<Record<string, string>>(
        (values, columnName) => ({ ...values, [columnName]: '' }),
        {},
      ),
      translatedValues: {},
    });
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New translation catalog</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Brand"
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
            disabled={isSubmitting}
            fullWidth
          >
            {BRANDS.map((brandOption) => (
              <MenuItem key={brandOption} value={brandOption}>
                {brandOption}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Catalog name"
            value={catalogName}
            onChange={(event) => setCatalogName(event.target.value)}
            disabled={isSubmitting}
            placeholder="i18n_email_welcome"
            fullWidth
          />
          <TextField
            label="Column names"
            value={columns}
            onChange={(event) => setColumns(event.target.value)}
            disabled={isSubmitting}
            helperText="Use one column per line or comma-separated values."
            minRows={4}
            multiline
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={!canCreate || isSubmitting} variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
